import type { PropertyHydraItem, PropertyTaskStateHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyReservationStatesHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyReservationMultiState/propertyReservationState.interface";
import type { ReportHydraItem } from "@verbleif/lib/src/composables/Api/Type/Report/report.interface";
import type { TaskHydraItem } from "@verbleif/lib/src/composables/Api/Type/Task/task.interface";
import type { SkeletonItem } from "@verbleif/lib/src/composables/Query/skeleton";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { createBatchLoader } from "@verbleif/shared/src/composables/Query/createBatchLoader";
import { createGlobalState } from "@vueuse/core";
import { localDateKey, nowIsoDatePoint, toIsoDatePoint } from "@web/composables/Date/localDateKey";
import { isAxiosError } from "axios";
import moment from "moment";
import { computed, ref } from "vue";
import { fetchPropertyReservationStates, fetchPropertyTaskStates } from "./propertyReservationStates.api";
import {
  clearPropertyStateMaps,
  CURRENT_KEY,
  fillReservationMap,
  MAP_SEP,
  parseReservationMapKey,
  PROPERTY_RESERVATION_STATE_ROOT,
  PROPERTY_TASK_STATE_ROOT,
  reservationDateKey,
  reservationMapKey,
  reservationQueryKey,
  reservationStateMap,
  taskMapKey,
  taskQueryKey,
  taskStateMap,
} from "./propertyStateMaps";

export { clearPropertyStateMaps, hydratePropertyStateMapsFromQueryClient } from "./propertyStateMaps";

type PropertyStateItem = TaskHydraItem | PropertyHydraItem | ReportHydraItem;

interface ReservationBatchKey {
  propertyId: number
  isoDatePoint: string
  isNow: boolean
  /** `reservationDateKey` of the date point: the batch loader shares one request per property and key. */
  dateKey: string
}

interface CachedItem<T> {
  data: T
  timestamp: number
}

const PROPERTY_STATE_STALE_TIME = 5 * 60_000;
const PROPERTY_STATE_GC_TIME = 30 * 60_000;
const MAX_PROPERTY_BATCH_SIZE = 100;
const BATCH_WINDOW_MS = 20;
const PROPERTY_STATE_PERSIST_META = { persist: true } as const;

function propertyIdOfItem(item: PropertyStateItem): number | null {
  if (item["@type"] === "Property") {
    return iriToId(item["@id"]);
  }
  const property = (item as TaskHydraItem | ReportHydraItem).property;
  return property ? iriToId(property) : null;
}

async function fetchReservationBatch(keys: ReservationBatchKey[]): Promise<Map<ReservationBatchKey, PropertyReservationStatesHydraItem | null>> {
  const params = new URLSearchParams();
  for (const key of keys) {
    params.append(`property[${key.propertyId}][]`, key.isoDatePoint);
  }

  const data = await fetchPropertyReservationStates(params);

  const result = new Map<ReservationBatchKey, PropertyReservationStatesHydraItem | null>();
  for (const key of keys) {
    const forProperty = data.member.filter(item => iriToId(item.property) === key.propertyId);
    const exact = forProperty.find(item => item.datePointAt === key.isoDatePoint);
    const matched = exact ?? forProperty.find((item) => {
      if (item.datePointAt === null) {
        return key.isNow;
      }
      return localDateKey(item.datePointAt) === localDateKey(key.isoDatePoint);
    });
    if (!matched) {
      result.set(key, null);
      continue;
    }
    result.set(key, key.isNow ? { ...matched, datePointAt: null } : matched);
  }
  return result;
}

async function fetchTaskBatch(propertyIds: number[]): Promise<Map<number, PropertyTaskStateHydraItem | null>> {
  const params = new URLSearchParams();
  for (const propertyId of propertyIds) {
    params.append("property[]", String(propertyId));
  }
  params.append("datePointAt", nowIsoDatePoint());

  const data = await fetchPropertyTaskStates(params);

  const byProperty = new Map<number, PropertyTaskStateHydraItem>();
  for (const item of data.member) {
    byProperty.set(iriToId(item.property), item);
  }

  const result = new Map<number, PropertyTaskStateHydraItem | null>();
  for (const propertyId of propertyIds) {
    result.set(propertyId, byProperty.get(propertyId) ?? null);
  }
  return result;
}

const reservationBatchLoader = createBatchLoader(fetchReservationBatch, {
  windowMs: BATCH_WINDOW_MS,
  maxBatchSize: MAX_PROPERTY_BATCH_SIZE,
  keyOf: key => `${key.propertyId}:${key.dateKey}`,
});

const taskBatchLoader = createBatchLoader(fetchTaskBatch, {
  windowMs: BATCH_WINDOW_MS,
  maxBatchSize: MAX_PROPERTY_BATCH_SIZE,
});

function withNow(datePoints: readonly string[], nowDatePoint: string): string[] {
  return datePoints.includes(nowDatePoint) ? [...datePoints] : [nowDatePoint, ...datePoints];
}

function collapseDatePoints(datePoints: readonly string[], nowDatePoint: string): string[] {
  const seen = new Set<string>();
  const collapsed: string[] = [];
  for (const datePoint of datePoints) {
    const key = reservationDateKey(datePoint, nowDatePoint);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    collapsed.push(datePoint);
  }
  return collapsed;
}

export const usePropertyReservationStates = createGlobalState(() => {
  const locationStore = useLocationStore();
  const loading = ref(false);
  const loadingBackground = ref(false);
  const loadingSidebar = ref(false);
  const loadingSingle = ref<number | null>(null);

  function currentLocationIri(): string {
    return locationStore.selectedLocation.value ?? "";
  }

  function reservationQueryOptions(
    locationIri: string,
    propertyId: number,
    isoDatePoint: string,
    nowDatePoint: string,
  ) {
    const isNow = isoDatePoint === nowDatePoint;
    const cacheKey = reservationDateKey(isoDatePoint, nowDatePoint);
    return {
      queryKey: reservationQueryKey(locationIri, propertyId, cacheKey),
      queryFn: async () => {
        const value = await reservationBatchLoader.load({
          propertyId,
          isoDatePoint,
          isNow,
          dateKey: cacheKey,
        });
        if (value) {
          fillReservationMap(locationIri, propertyId, cacheKey, value);
        }
        return value;
      },
      staleTime: PROPERTY_STATE_STALE_TIME,
      gcTime: PROPERTY_STATE_GC_TIME,
      meta: PROPERTY_STATE_PERSIST_META,
    };
  }

  function taskQueryOptions(locationIri: string, propertyId: number) {
    return {
      queryKey: taskQueryKey(locationIri, propertyId),
      queryFn: async () => {
        const value = await taskBatchLoader.load(propertyId);
        taskStateMap.set(taskMapKey(locationIri, propertyId), value);
        return value;
      },
      staleTime: PROPERTY_STATE_STALE_TIME,
      gcTime: PROPERTY_STATE_GC_TIME,
      meta: PROPERTY_STATE_PERSIST_META,
    };
  }

  function hydrateReservationFromQuery(
    locationIri: string,
    propertyId: number,
    dateKey: string,
  ): PropertyReservationStatesHydraItem | null | undefined {
    const data = getQueryClient().getQueryData<PropertyReservationStatesHydraItem | null>(
      reservationQueryKey(locationIri, propertyId, dateKey),
    );
    if (data) {
      fillReservationMap(locationIri, propertyId, dateKey, data);
    }
    return data;
  }

  function hydrateReservationByDateKey(
    locationIri: string,
    propertyId: number,
    dateKey: string,
  ): PropertyReservationStatesHydraItem | null {
    const existing = reservationStateMap.get(reservationMapKey(locationIri, propertyId, dateKey));
    if (existing) {
      return existing;
    }

    const exact = hydrateReservationFromQuery(locationIri, propertyId, dateKey);
    if (exact) {
      return exact;
    }

    const matches = getQueryClient().getQueriesData<PropertyReservationStatesHydraItem | null>({
      queryKey: [PROPERTY_RESERVATION_STATE_ROOT, locationIri, propertyId],
    });
    for (const [queryKey, data] of matches) {
      if (!data) {
        continue;
      }
      const queryCacheKey = String(queryKey[3] ?? "");
      if (dateKey === CURRENT_KEY) {
        if (data.datePointAt === null || queryCacheKey === CURRENT_KEY) {
          fillReservationMap(locationIri, propertyId, CURRENT_KEY, data);
          return data;
        }
        continue;
      }
      const dataDay = data.datePointAt ? localDateKey(data.datePointAt) : null;
      if (dataDay === dateKey || queryCacheKey === dateKey) {
        fillReservationMap(locationIri, propertyId, dateKey, data);
        return data;
      }
    }
    return null;
  }

  function reservationStatesForProperty(propertyId: number): PropertyReservationStatesHydraItem[] {
    const locationIri = currentLocationIri();
    const prefix = `${locationIri}${MAP_SEP}${propertyId}${MAP_SEP}`;
    const states: PropertyReservationStatesHydraItem[] = [];
    for (const [key, state] of reservationStateMap) {
      if (key.startsWith(prefix)) {
        states.push(state);
      }
    }
    return states;
  }

  const propertyReservationStates = computed(() => {
    const locationIri = currentLocationIri();
    const result: Record<number, { data: PropertyReservationStatesHydraItem[], timestamp: number, datePointAt: string[] }> = {};
    for (const [key, state] of reservationStateMap) {
      const parsed = parseReservationMapKey(key);
      if (parsed.locationIri !== locationIri) {
        continue;
      }
      const bucket = result[parsed.propertyId] ??= {
        data: [],
        timestamp: getQueryClient().getQueryState(
          reservationQueryKey(locationIri, parsed.propertyId, parsed.dateKey),
        )?.dataUpdatedAt ?? Date.now(),
        datePointAt: [],
      };
      bucket.data.push(state);
      if (parsed.dateKey !== CURRENT_KEY) {
        bucket.datePointAt.push(parsed.dateKey);
      }
    }
    return result;
  });

  const propertyTaskStates = computed(() => {
    const locationIri = currentLocationIri();
    const result: Record<number, CachedItem<PropertyTaskStateHydraItem | null>> = {};
    for (const [key, state] of taskStateMap) {
      const [mapLocation, propertyId] = key.split(MAP_SEP);
      if (mapLocation !== locationIri) {
        continue;
      }
      result[Number(propertyId)] = {
        data: state,
        timestamp: getQueryClient().getQueryState(taskQueryKey(locationIri, Number(propertyId)))?.dataUpdatedAt ?? Date.now(),
      };
    }
    return result;
  });

  function getPropertyTaskState(propertyId: number): PropertyTaskStateHydraItem | null {
    const locationIri = currentLocationIri();
    const mapKey = taskMapKey(locationIri, propertyId);
    if (taskStateMap.has(mapKey)) {
      return taskStateMap.get(mapKey) ?? null;
    }
    const data = getQueryClient().getQueryData<PropertyTaskStateHydraItem | null>(taskQueryKey(locationIri, propertyId));
    if (data !== undefined) {
      taskStateMap.set(mapKey, data);
      return data;
    }
    return null;
  }

  function hasPropertyState(propertyId: number): boolean {
    if (reservationStatesForProperty(propertyId).length > 0) {
      return true;
    }
    const locationIri = currentLocationIri();
    const matches = getQueryClient().getQueriesData<PropertyReservationStatesHydraItem | null>({
      queryKey: [PROPERTY_RESERVATION_STATE_ROOT, locationIri, propertyId],
    });
    for (const [queryKey, data] of matches) {
      if (!data) {
        continue;
      }
      fillReservationMap(locationIri, propertyId, String(queryKey[3] ?? ""), data);
    }
    return reservationStatesForProperty(propertyId).length > 0;
  }

  function getPropertyReservationStateForDeadline(propertyId: number, deadline?: string | null): PropertyReservationStatesHydraItem | null {
    const locationIri = currentLocationIri();
    if (!deadline) {
      return hydrateReservationByDateKey(locationIri, propertyId, CURRENT_KEY);
    }
    const dateKey = localDateKey(deadline);
    if (dateKey === null) {
      return null;
    }
    return hydrateReservationByDateKey(locationIri, propertyId, dateKey);
  }

  async function loadReservationKeys(
    keys: Array<{ propertyId: number, isoDatePoint: string }>,
    nowDatePoint: string,
    background: boolean,
  ): Promise<void> {
    const locationIri = currentLocationIri();
    const client = getQueryClient();
    const propertyIds = [...new Set(keys.map(key => key.propertyId))];
    await Promise.all([
      ...keys.map((key) => {
        const options = reservationQueryOptions(locationIri, key.propertyId, key.isoDatePoint, nowDatePoint);
        return background ? client.prefetchQuery(options) : client.ensureQueryData(options);
      }),
      ...propertyIds.map((propertyId) => {
        const options = taskQueryOptions(locationIri, propertyId);
        return background ? client.prefetchQuery(options) : client.ensureQueryData(options);
      }),
    ]);
  }

  function collectItemKeys(
    items: (TaskHydraItem | SkeletonItem | PropertyHydraItem | ReportHydraItem)[],
    nowDatePoint: string,
  ): Array<{ propertyId: number, isoDatePoint: string }> {
    const taskDeadlinesByProperty: Record<number, string[]> = {};
    const propertyIds = new Set<number>();

    for (const item of items) {
      if ("skeleton" in item || !("@id" in item)) {
        continue;
      }
      const propertyId = propertyIdOfItem(item);
      if (propertyId === null) {
        continue;
      }
      propertyIds.add(propertyId);

      if (item["@type"] !== "Task") {
        continue;
      }
      const deadline = (item as TaskHydraItem).deadlineAt ? toIsoDatePoint((item as TaskHydraItem).deadlineAt as string) : nowDatePoint;
      if (deadline === null) {
        continue;
      }
      const deadlines = taskDeadlinesByProperty[propertyId] ??= [];
      if (!deadlines.includes(deadline)) {
        deadlines.push(deadline);
      }
    }

    const keys: Array<{ propertyId: number, isoDatePoint: string }> = [];
    for (const propertyId of propertyIds) {
      for (const isoDatePoint of collapseDatePoints(withNow(taskDeadlinesByProperty[propertyId] ?? [], nowDatePoint), nowDatePoint)) {
        keys.push({ propertyId, isoDatePoint });
      }
    }
    return keys;
  }

  async function getPropertyReservationStates(
    items: (TaskHydraItem | SkeletonItem | PropertyHydraItem | ReportHydraItem)[],
    options: {
      background?: boolean
      /**
       * Load and await these items even while the overview's own load runs, without touching its loading flags.
       * For callers that need every item's state, such as an export; failures other than 401/403 are thrown.
       */
      independent?: boolean
    } = {},
  ): Promise<void> {
    const isBackground = options.background === true;
    const isIndependent = options.independent === true;
    if (!isBackground && !isIndependent && loading.value) {
      return;
    }

    const nowDatePoint = nowIsoDatePoint();
    const keys = collectItemKeys(items, nowDatePoint);
    if (keys.length === 0) {
      return;
    }

    if (isIndependent) {
      try {
        await loadReservationKeys(keys, nowDatePoint, false);
      } catch (error) {
        if (isAxiosError(error) && [403, 401].includes(error.response?.status ?? 0)) {
          return;
        }
        throw error;
      }
      return;
    }

    try {
      if (isBackground) {
        loadingBackground.value = true;
      } else {
        loading.value = true;
      }
      await loadReservationKeys(keys, nowDatePoint, isBackground);
    } catch (error) {
      if (isAxiosError(error) && [403, 401].includes(error.response?.status ?? 0)) {
        return;
      }
      throw error;
    } finally {
      if (isBackground) {
        loadingBackground.value = false;
      } else {
        loading.value = false;
      }
    }
  }

  function getSidebarReservationState(propertyId: number): PropertyReservationStatesHydraItem | null {
    return getPropertyReservationStateForDeadline(propertyId);
  }

  function hasSidebarReservationState(propertyId: number): boolean {
    return getSidebarReservationState(propertyId) !== null;
  }

  function getSidebarTaskState(propertyId: number): PropertyTaskStateHydraItem | null {
    return getPropertyTaskState(propertyId);
  }

  const sidebarStateQueue = new Set<number>();
  let sidebarStateFlush: Promise<void> | null = null;

  function queueSidebarPropertyStates(propertyIds: readonly number[]): Promise<void> {
    for (const propertyId of propertyIds) {
      sidebarStateQueue.add(propertyId);
    }
    if (sidebarStateFlush) {
      return sidebarStateFlush;
    }
    sidebarStateFlush = flushSidebarPropertyStates().finally(() => {
      sidebarStateFlush = null;
      if (sidebarStateQueue.size > 0) {
        void queueSidebarPropertyStates([]);
      }
    });
    return sidebarStateFlush;
  }

  async function flushSidebarPropertyStates(): Promise<void> {
    loadingSidebar.value = true;
    try {
      while (sidebarStateQueue.size > 0) {
        const propertyIds = [...sidebarStateQueue];
        sidebarStateQueue.clear();
        const nowDatePoint = moment().toISOString();
        const richDatePoints = [
          moment(nowDatePoint).subtract(1, "day").toISOString(),
          nowDatePoint,
          moment(nowDatePoint).add(1, "day").toISOString(),
          moment(nowDatePoint).add(2, "days").toISOString(),
        ];
        const keys = propertyIds.flatMap(propertyId =>
          richDatePoints.map(isoDatePoint => ({ propertyId, isoDatePoint })),
        );
        try {
          await loadReservationKeys(keys, nowDatePoint, true);
        } catch (error) {
          if (isAxiosError(error) && [403, 401].includes(error.response?.status ?? 0)) {
            return;
          }
          throw error;
        }
      }
    } finally {
      loadingSidebar.value = false;
    }
  }

  async function getPropertyReservationStatesForSidebar(properties: PropertyHydraItem[]): Promise<void> {
    await queueSidebarPropertyStates(properties.map(property => iriToId(property["@id"])));
  }

  async function getPropertyReservationState(propertyId: number): Promise<{
    reservationState: PropertyReservationStatesHydraItem | null
    taskState: PropertyTaskStateHydraItem | null
  } | null> {
    const existing = getPropertyReservationStateForDeadline(propertyId);
    const existingTask = getPropertyTaskState(propertyId);
    if (existing) {
      return { reservationState: existing, taskState: existingTask };
    }

    if (loadingSingle.value) {
      return null;
    }

    try {
      loadingSingle.value = propertyId;
      const nowDatePoint = nowIsoDatePoint();
      await loadReservationKeys([{ propertyId, isoDatePoint: nowDatePoint }], nowDatePoint, false);
      return {
        reservationState: getPropertyReservationStateForDeadline(propertyId),
        taskState: getPropertyTaskState(propertyId),
      };
    } catch (error) {
      if (isAxiosError(error) && [403, 401].includes(error.response?.status ?? 0)) {
        return null;
      }
      throw error;
    } finally {
      loadingSingle.value = null;
    }
  }

  async function refreshPropertyStateForDeadline(propertyId: number, deadline?: string | null): Promise<void> {
    const nowDatePoint = nowIsoDatePoint();
    const deadlineIso = deadline ? toIsoDatePoint(deadline) : null;
    const datePoints = deadlineIso ? withNow([deadlineIso], nowDatePoint) : [nowDatePoint];
    const locationIri = currentLocationIri();
    const client = getQueryClient();
    await Promise.all(datePoints.map(isoDatePoint => client.fetchQuery(
      reservationQueryOptions(locationIri, propertyId, isoDatePoint, nowDatePoint),
    )));
    await client.fetchQuery(taskQueryOptions(locationIri, propertyId));
  }

  async function forceRefreshPropertyForTaskUpdate(propertyId: number, deadline?: string | null): Promise<void> {
    const nowDatePoint = nowIsoDatePoint();
    const deadlineIso = deadline ? toIsoDatePoint(deadline) : null;
    const datePoints = deadlineIso ? withNow([deadlineIso], nowDatePoint) : [nowDatePoint];
    const locationIri = currentLocationIri();
    const client = getQueryClient();
    await Promise.all([
      client.invalidateQueries({ queryKey: [PROPERTY_TASK_STATE_ROOT, locationIri, propertyId] }),
      client.invalidateQueries({ queryKey: [PROPERTY_RESERVATION_STATE_ROOT, locationIri, propertyId], refetchType: "none" }),
    ]);
    await Promise.all([
      client.fetchQuery(taskQueryOptions(locationIri, propertyId)),
      ...datePoints.map(isoDatePoint => client.fetchQuery(
        reservationQueryOptions(locationIri, propertyId, isoDatePoint, nowDatePoint),
      )),
    ]);
  }

  function resetPropertyStatesCache(): void {
    const client = getQueryClient();
    const now = Date.now();
    for (const query of client.getQueryCache().getAll()) {
      const root = query.queryKey[0];
      if (root !== PROPERTY_RESERVATION_STATE_ROOT && root !== PROPERTY_TASK_STATE_ROOT) {
        continue;
      }
      if (now - query.state.dataUpdatedAt > PROPERTY_STATE_STALE_TIME) {
        client.removeQueries({ queryKey: query.queryKey, exact: true });
      }
    }
    for (const key of [...reservationStateMap.keys()]) {
      const parsed = parseReservationMapKey(key);
      const stillPresent = client.getQueriesData({
        queryKey: [PROPERTY_RESERVATION_STATE_ROOT, parsed.locationIri, parsed.propertyId],
      }).some(([, data]) => Boolean(data));
      if (!stillPresent) {
        reservationStateMap.delete(key);
      }
    }
    for (const key of [...taskStateMap.keys()]) {
      const [locationIri, propertyId] = key.split(MAP_SEP);
      if (client.getQueryData(taskQueryKey(locationIri, Number(propertyId))) === undefined) {
        taskStateMap.delete(key);
      }
    }
  }

  function clearAllPropertyStatesCache(): void {
    clearPropertyStateMaps();
    const client = getQueryClient();
    client.removeQueries({ queryKey: [PROPERTY_RESERVATION_STATE_ROOT] });
    client.removeQueries({ queryKey: [PROPERTY_TASK_STATE_ROOT] });
  }

  function getAllPropertyStatesCache(): {
    sidebarPropertyStatesCache: Record<string, {
      reservationStates: PropertyReservationStatesHydraItem[]
      taskState: PropertyTaskStateHydraItem | null
      timestamp: number
      fetchedDatePoints: string[]
    }>
    taskPropertyStatesCache: Record<string, {
      reservationStates: PropertyReservationStatesHydraItem[]
      taskState: PropertyTaskStateHydraItem | null
      timestamp: number
      fetchedDatePoints: string[]
    }>
  } {
    const reconstructed: Record<string, {
      reservationStates: PropertyReservationStatesHydraItem[]
      taskState: PropertyTaskStateHydraItem | null
      timestamp: number
      fetchedDatePoints: string[]
    }> = {};
    for (const [key, state] of reservationStateMap) {
      const parsed = parseReservationMapKey(key);
      const bucket = reconstructed[parsed.propertyId] ??= {
        reservationStates: [],
        taskState: taskStateMap.get(taskMapKey(parsed.locationIri, parsed.propertyId)) ?? null,
        timestamp: Date.now(),
        fetchedDatePoints: [],
      };
      bucket.reservationStates.push(state);
      if (parsed.dateKey !== CURRENT_KEY) {
        bucket.fetchedDatePoints.push(parsed.dateKey);
      }
    }
    return {
      sidebarPropertyStatesCache: { ...reconstructed },
      taskPropertyStatesCache: { ...reconstructed },
    };
  }

  function reset(): void {
    resetPropertyStatesCache();
  }

  async function forceRefreshPropertiesBatched(
    propertyDatePointsMap: Map<number, string[]>,
    onBatchProgress?: (completed: number, total: number, batchIndex: number, totalBatches: number) => void,
  ): Promise<{ successCount: number, errorCount: number }> {
    const keys: Array<{ propertyId: number, isoDatePoint: string }> = [];
    propertyDatePointsMap.forEach((datePoints, propertyId) => {
      for (const isoDatePoint of new Set(datePoints)) {
        keys.push({ propertyId, isoDatePoint });
      }
    });

    if (keys.length === 0) {
      return { successCount: 0, errorCount: 0 };
    }

    const batches: Array<Array<{ propertyId: number, isoDatePoint: string }>> = [];
    for (let index = 0; index < keys.length; index += MAX_PROPERTY_BATCH_SIZE) {
      batches.push(keys.slice(index, index + MAX_PROPERTY_BATCH_SIZE));
    }

    let successCount = 0;
    let errorCount = 0;
    const nowDatePoint = nowIsoDatePoint();
    const locationIri = currentLocationIri();
    const client = getQueryClient();

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      try {
        await Promise.all(batch.map(key => client.fetchQuery(
          reservationQueryOptions(locationIri, key.propertyId, key.isoDatePoint, nowDatePoint),
        )));
        await Promise.all([...new Set(batch.map(key => key.propertyId))].map(propertyId => client.fetchQuery(
          taskQueryOptions(locationIri, propertyId),
        )));
        successCount += batch.length;
      } catch (error) {
        console.error(`Batch ${batchIndex + 1} failed:`, error);
        errorCount += batch.length;
      }
      onBatchProgress?.(batchIndex + 1, batches.length, batchIndex + 1, batches.length);
    }

    return { successCount, errorCount };
  }

  return {
    propertyReservationStates,
    propertyTaskStates,
    getPropertyTaskState,
    getSidebarReservationState,
    hasSidebarReservationState,
    getSidebarTaskState,
    queueSidebarPropertyStates,
    hasPropertyState,
    getPropertyReservationStates,
    getPropertyReservationState,
    getPropertyReservationStateForDeadline,
    getPropertyReservationStatesForSidebar,
    resetPropertyStatesCache,
    clearAllPropertyStatesCache,
    getAllPropertyStatesCache,
    refreshPropertyStateForDeadline,
    forceRefreshPropertyForTaskUpdate,
    forceRefreshPropertiesBatched,
    loading,
    loadingBackground,
    loadingSidebar,
    loadingSingle,
    reset,
  };
});
